import * as Icon from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Menu, message } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import * as api from '../../../api';
import { useAccountContext, useSessionID } from '../../../components/contexts/AccountContextManager';
import useTranslatedString from '../../../utilities/useTranslatedString';

const StyledMenu = styled(Menu)`
    && {
        padding-right: 24px;
    }

    .ant-menu-item {
        border-radius: 0 10px 10px 0;
        margin-right: 20px;
        color: #b2b2b2;

        svg {
            fill: #b2b2b2;
        }
    }

    &.li.ant-menu-item.ant-menu-item-only-child {
        height: 'auto';
    }

    &.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
        color: var(--ant-primary-color);
        background-color: #eff0f1;

        svg {
            fill: var(--ant-primary-color);
        }
    }

    &.ant-menu-inline .ant-menu-item::after {
        border: none;
    }

    &.ant-menu-inline .ant-menu-item::before {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        border-left: 3px solid var(--ant-primary-color);
        transform: scaleY(0.0001);
        opacity: 0;
        transition:
            transform 0.15s cubic-bezier(0.215, 0.61, 0.355, 1),
            opacity 0.15s cubic-bezier(0.215, 0.61, 0.355, 1);
        content: '';
    }

    &.ant-menu-inline .ant-menu-item-selected::before {
        transform: scaleY(1);
        opacity: 1;
        transform:
            0.15s cubic-bezier(0.645, 0.045, 0.355, 1),
            opacity 0.15s cubic-bezier(0.645, 0.045, 0.355, 1);
    }
`;

type CIMenuItemsProps = {
    onMenuItemClicked: (path: string) => void;
    menuItems: api.MenuItemSpecsFragment[];
};

const handleCustomPath = (menuItem: api.MenuItemSpecsFragment) => {
    if (menuItem.__typename !== 'MenuCustomPathItem') {
        return;
    }

    window.open(menuItem.pathname, '_blank');
};

const getMenuIcon = (menuItem: api.MenuItemSpecsFragment) => {
    // oxlint-disable-next-line import/namespace
    const DynamicIcon = Icon[menuItem.icon];

    return <DynamicIcon />;
};

const CIMenuItems = ({ onMenuItemClicked, menuItems }: CIMenuItemsProps) => {
    const translate = useTranslatedString();

    const { t } = useTranslation('common');

    const apolloClient = useApolloClient();
    const { logout } = useAccountContext();
    const navigate = useNavigate();

    const sessionId = useSessionID();

    const handleSignOut = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            apolloClient
                .mutate<api.RevokeSessionMutation, api.RevokeSessionMutationVariables>({
                    mutation: api.RevokeSessionDocument,
                    variables: { sessionId, displayNotice: false },
                })
                .then(() => {
                    // first unset the session
                    logout();

                    // then go to sign in page
                    navigate('/auth/signIn', { replace: true, state: { noWebAuthn: true } });

                    // and finally display a message
                    message.success({
                        content: t('common:messages.successLogout'),
                        key: 'primary',
                    });
                });
        },
        [apolloClient, logout, navigate, sessionId, t]
    );

    const menuAction = useCallback(
        (item: api.MenuItemSpecsFragment, event?: React.MouseEvent<HTMLButtonElement>) => {
            switch (item.__typename) {
                case 'MenuCustomPathItem':
                    handleCustomPath(item);
                    break;

                case 'MenuEndpointItem':
                    onMenuItemClicked(item.endpoint.pathname);
                    break;

                case 'MenuLogoutActionItem':
                    handleSignOut(event);
                    break;

                default:
                    return null;
            }

            return null;
        },
        [handleSignOut, onMenuItemClicked]
    );

    const routes = useMemo(
        () =>
            menuItems.map(
                item => ({
                    label: translate(item.label),
                    key: item.id,
                    icon: getMenuIcon(item),
                    onClick: event => menuAction(item, event),
                }),
                []
            ),
        [handleSignOut, menuItems, onMenuItemClicked, translate]
    );

    return <StyledMenu defaultSelectedKeys={['new']} items={routes} mode="inline" />;
};
export default CIMenuItems;
